import * as React from 'react'
import { observer } from 'mobx-react'
import { Switch, Route } from 'react-router-dom'
import { AppContent, Layout } from '@pushly/aqe/lib/components'
import { Sidebar } from './sidebar'
import { Dashboard } from './dashboard'
import { Accounts } from './accounts'
import { Account } from './account'
import { InternalRoute } from '@halo/components/router/route'
import { classnames } from '@halo/_utils/classnames'
import { BatchBilling } from '@halo/portals/post-auth/batch-billing'
import { User } from '@halo/portals/post-auth/user'
import { Domains } from '@halo/portals/post-auth/domains'
import { Users } from '@halo/portals/post-auth/users'
import Sandbox from '@halo/portals/post-auth/sandbox'
import Jobs from '@halo/portals/post-auth/jobs'
import { WhiteLabels } from '@halo/portals/post-auth/white-labels'
import { PlatformMessages } from '@halo/portals/post-auth/platform-messages'
import PageAnalysisConfigsListPage from '@halo/portals/post-auth/page-analysis-configs/page-analysis-configs-list.page'
import EditPageAnalysisConfigPage from '@halo/portals/post-auth/page-analysis-configs/edit-page-analysis-config.page'
import EditPageAnalysisFieldPage from '@halo/portals/post-auth/page-analysis-configs/edit-page-analysis-field.page'
import { Domain } from '@halo/portals/post-auth/domains/domain'
import { Flags } from '@halo/portals/post-auth/flags/flags'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc/es5'
import { AccessControl } from '@halo/portals/post-auth/access-control'
import UserRoleView from '@halo/components/user-role-view/user-role-view'
import AccessPolicyView from '@halo/components/access-policy-view/access-policy-view'
import { AppService } from '@halo/services/app.service'
import { AccessRoleId } from '@halo/enums/access-role.enum'
import { FeedDataForm } from '@halo/portals/post-auth/domains/feeds/feed-data-form'
import { FeedPage } from '@halo/portals/post-auth/domains/feeds/feed-page'
import { ErrorPage } from '@halo/components/error-page/error-page'
import { AbilityContext } from '@halo/stores/ability.state'
import { Ability } from '@casl/ability'

@observer
export class PostAuth extends React.Component {
    protected readonly appState: AppState
    protected readonly appService: AppService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render() {
        return (
            <AbilityContext.Provider value={this.appState.abilityStore.abilities ?? new Ability()}>
                <div className={classnames('portal', 'post-auth')}>
                    <Layout>
                        <Sidebar {...this.props} />

                        <AppContent>
                            <Switch>
                                <InternalRoute
                                    path="/organizations/:accountId([\d]+)"
                                    exact={true}
                                    component={
                                        this.appService.currentUserCanReadAtLeastOneOrg()
                                            ? Account
                                            : () => ErrorPage({ errorCode: 403 })
                                    }
                                />
                                <InternalRoute
                                    path="/organizations"
                                    exact={true}
                                    component={
                                        this.appService.currentUserCanReadAtLeastOneOrg()
                                            ? Accounts
                                            : () => ErrorPage({ errorCode: 403 })
                                    }
                                />
                                <InternalRoute
                                    path={[
                                        '/organizations/:accountId([\\d]+)/domains/:domainId([\\d]+)',
                                        '/organizations/:accountId([\\d]+)/domains/:domainId([\\d]+)/:activeTab([\\w]+)',
                                    ]}
                                    exact={true}
                                    component={Domain}
                                />
                                <InternalRoute
                                    path="/organizations/:accountId([\d]+)/users/:userId([\d]+)"
                                    exact={true}
                                    component={User}
                                />

                                <InternalRoute
                                    path="/domains"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(Domains)}
                                />
                                <InternalRoute
                                    path={[
                                        '/domains/:domainId([\\d]+)',
                                        '/domains/:domainId([\\d]+)/:activeTab([\\w-]+)',
                                    ]}
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(Domain)}
                                />

                                <InternalRoute
                                    path="/domains/:domainId([\d]+)/feeds/new"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(FeedDataForm)}
                                />

                                <InternalRoute
                                    path="/domains/:domainId([\d]+)/feeds/:feedId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(FeedPage)}
                                />

                                <InternalRoute
                                    path="/domains/:domainId([\d]+)/feeds/:feedId([\d]+)/edit"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(FeedDataForm)}
                                />

                                <InternalRoute
                                    path="/users"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(Users)}
                                />
                                <InternalRoute
                                    path="/users/:userId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternal(User)}
                                />

                                <InternalRoute
                                    path="/page-analysis-configs"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(PageAnalysisConfigsListPage)}
                                />
                                <InternalRoute
                                    path="/page-analysis-configs/:pageAnalysisConfigId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(EditPageAnalysisConfigPage)}
                                />
                                <InternalRoute
                                    path="/page-analysis-configs/:pageAnalysisConfigId([\d]+)/field-configs/:fieldConfigId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(EditPageAnalysisFieldPage)}
                                />
                                <InternalRoute
                                    path="/page-analysis-configs/:pageAnalysisConfigId([\d]+)/field-configs/new"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(EditPageAnalysisFieldPage)}
                                />

                                <InternalRoute
                                    path="/batch-billing"
                                    exact={true}
                                    component={this.ensureActingUserCanAccessBatchBilling(BatchBilling)}
                                />

                                <InternalRoute
                                    path="/white-labels"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(WhiteLabels)}
                                />

                                <InternalRoute
                                    path="/sandbox"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(Sandbox)}
                                />

                                <InternalRoute
                                    path={['/dev/jobs', '/dev/jobs/:activeTab']}
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(Jobs)}
                                />

                                <InternalRoute
                                    path="/access-control/user-roles/:userRoleId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(UserRoleView)}
                                />
                                <InternalRoute
                                    path="/access-control/access-policies/:accessPolicyId([\d]+)"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(AccessPolicyView)}
                                />
                                <InternalRoute
                                    path={['/access-control', '/access-control/:activeTab']}
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(AccessControl)}
                                />

                                <InternalRoute
                                    path="/platform-messages"
                                    exact={true}
                                    component={this.ensureActingUserCanAccessPlatformMessages(PlatformMessages)}
                                />

                                <InternalRoute
                                    path="/flags"
                                    exact={true}
                                    component={this.ensureActingUserIsInternalDev(Flags)}
                                />

                                <InternalRoute path="/" exact={true} component={Dashboard} />

                                <Route component={() => ErrorPage({ errorCode: 403 })} />
                            </Switch>
                        </AppContent>
                    </Layout>
                </div>
            </AbilityContext.Provider>
        )
    }

    protected ensureActingUserIsInternal(component: any) {
        return this.appState.currentUser?.isInternalUser ? component : () => ErrorPage({ errorCode: 403 })
    }

    protected ensureActingUserIsInternalDev(component: any) {
        return this.appState.currentUser?.isInternalDevUser ? component : () => ErrorPage({ errorCode: 403 })
    }

    protected ensureActingUserCanAccessBatchBilling(component: any) {
        const can =
            this.appState.currentUser?.isInternalUser &&
            [
                AccessRoleId.INTERNAL_DEVELOPER,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER,
            ].includes(this.appState.currentUser?.user_role_id)

        return can ? component : () => ErrorPage({ errorCode: 403 })
    }

    protected ensureActingUserCanAccessPlatformMessages(component: any) {
        const can =
            this.appState.currentUser?.isInternalUser &&
            [AccessRoleId.INTERNAL_DEVELOPER, AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD].includes(
                this.appState.currentUser?.user_role_id,
            )

        return can ? component : () => ErrorPage({ errorCode: 403 })
    }
}
